<script setup lang="ts">
import { computed, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'

import { useAuthStore } from '../stores/auth'
import { useLoginModalStore } from '../stores/loginModal'
import MemberTierBadge from '../components/MemberTierBadge.vue'
import { brand } from '../config/brand'
import { workbenchNavPrimary, workbenchNavSecondary } from './workbenchNav'
import { workbenchPath } from './workbenchPaths'
import { WORKBENCH_DEFAULT_INVITATION_CODE } from './workbenchConfig'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const loginModal = useLoginModalStore()

const avatarUrl = computed(() => (auth.me?.avatar_url || '').trim())
const isLoggedIn = computed(() => Boolean(avatarUrl.value || (auth.me?.nick_name || '').trim() || (auth.me?.phone || '').trim()))
const userName = computed(() => {
  if (!isLoggedIn.value) return '请登陆'
  return (auth.me?.nick_name || auth.me?.phone || '未登录').trim()
})
const userLevel = computed(() => {
  if (!isLoggedIn.value) return `欢迎使用${brand.workbenchName}`
  return (auth.me?.type || 'Pro Plan Member').trim()
})
const memberTierRaw = computed(() => String((auth.me as any)?.membership || (auth.me as any)?.level || auth.me?.type || 'free').trim())
const isAccountActive = computed(() => String(route.path || '').startsWith('/ai-workbench/account') || String(route.path || '').startsWith('/account'))

function withBase(p: string) {
  return `${import.meta.env.BASE_URL || '/'}${String(p || '').replace(/^\//, '')}`
}

function iconStyle(name: string) {
  const url = withBase(`/assets/workbench-icons/${name}.svg`)
  return {
    WebkitMaskImage: `url(${url})`,
    maskImage: `url(${url})`,
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskSize: 'contain',
    maskSize: 'contain',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
    backgroundColor: 'currentColor',
  } as any
}

function iconClass(active: boolean) {
  return ['w-[22px] h-[22px] flex-none', active ? 'text-highlight' : ''].join(' ')
}

function navClass(active: boolean) {
  return [
    'flex items-center gap-3 px-4 py-3 text-on-surface-variant hover:bg-white/6 transition-colors rounded-lg font-semibold tracking-wide',
    active ? 'bg-white/10 text-on-surface' : '',
  ].join(' ')
}

const navBusy = ref(false)

async function ensureMe() {
  if (!auth.token) {
    try {
      await auth.tryRestoreLoginState()
    } catch {}
  }
  if (!auth.token) throw new Error('not_logged_in')
  await auth.fetchMe()
}

async function openAccount() {
  if (navBusy.value) return
  navBusy.value = true
  try {
    await ensureMe()
    await router.push(workbenchPath('/account'))
  } catch {
    loginModal.show({
      nextPath: workbenchPath('/account'),
      cancelPath: workbenchPath('/tools'),
      tab: 'sms',
      defaultInvitationCode: WORKBENCH_DEFAULT_INVITATION_CODE,
    })
  } finally {
    navBusy.value = false
  }
}
</script>

<template>
  <aside class="w-[280px] h-screen sticky top-0 left-0 flex flex-col py-6 px-4 gap-2 bg-ink border-r border-white/10 font-manrope">
    <div class="px-4 mb-8">
      <RouterLink :to="workbenchPath('/tools')" class="flex items-center gap-3">
        <span
          v-if="brand.key === 'wuhenai'"
          class="w-10 h-10 rounded-xl overflow-hidden shadow-[0_4px_18px_rgba(91,35,255,0.55)] flex-none flex items-center justify-center"
          aria-hidden="true"
        >
          <svg width="40" height="40" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" class="w-full h-full" style="display:block">
            <defs>
              <linearGradient id="lg-workbench-wuhenai" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stop-color="#5B23FF" />
                <stop offset="1" stop-color="#008BFF" />
              </linearGradient>
            </defs>
            <rect x="16" y="16" width="480" height="480" rx="120" fill="url(#lg-workbench-wuhenai)" />
            <g transform="rotate(-45 256 256)">
              <path
                d="M224,133 L288,133 A32,32 0 0 1 320,165 L320,287 L192,287 L192,165 A32,32 0 0 1 224,133 Z"
                fill="#FFFFFF"
              />
              <path
                d="M192,292 L320,292 L320,347 A32,32 0 0 1 288,379 L224,379 A32,32 0 0 1 192,347 Z"
                fill="#FFFFFF"
              />
            </g>
            <path
              d="M400,48 Q400,112 464,112 Q400,112 400,176 Q400,112 336,112 Q400,112 400,48 Z"
              fill="#E4FF30"
            />
          </svg>
        </span>
        <img v-else :src="withBase(brand.logoPath)" :alt="brand.productName" class="w-10 h-10 rounded-xl object-contain flex-none" />
        <div class="min-w-0">
          <h1 class="text-xl font-bold text-on-surface truncate">{{ brand.workbenchName }}</h1>
          <p class="text-xs text-on-surface-variant opacity-70 truncate">{{ brand.slogan }}</p>
        </div>
      </RouterLink>
    </div>

    <div class="flex-1 min-h-0 grid grid-rows-[auto_1fr_auto]">
      <nav class="flex flex-col gap-1" aria-label="Primary">
        <RouterLink
          v-for="it in workbenchNavPrimary"
          :key="it.name"
          :to="it.to"
          :class="navClass(route.name === it.name)"
        >
          <span :class="iconClass(route.name === it.name)" aria-hidden="true" :style="iconStyle(it.icon || '')" />
          {{ it.label }}
        </RouterLink>
      </nav>

      <div class="min-h-0"></div>

      <div class="flex flex-col gap-1 pt-4 mb-4" aria-label="Secondary">
        <RouterLink
          v-for="it in workbenchNavSecondary"
          :key="it.name"
          :to="it.to"
          :class="navClass(route.name === it.name)"
        >
          <span :class="iconClass(route.name === it.name)" aria-hidden="true" :style="iconStyle(it.icon || '')" />
          {{ it.label }}
        </RouterLink>
      </div>
    </div>

    <button
      class="px-4 py-4 flex items-center gap-3 rounded-xl border transition-colors"
      :class="isAccountActive ? 'bg-white/10 border-white/10 text-on-surface' : 'bg-white/6 border-white/10 hover:bg-white/8'"
      @click="openAccount"
    >
      <div class="w-10 h-10 rounded-full overflow-hidden bg-white/10 grid place-items-center">
        <img v-if="avatarUrl" :src="avatarUrl" alt="" class="w-full h-full object-cover" />
        <svg v-else width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M20 21a8 8 0 1 0-16 0"
            stroke="rgba(255,255,255,0.86)"
            stroke-width="2"
            stroke-linecap="round"
          />
          <path
            d="M12 11a4 4 0 1 0-4-4 4 4 0 0 0 4 4Z"
            stroke="rgba(255,255,255,0.86)"
            stroke-width="2"
          />
        </svg>
      </div>
      <div class="flex flex-col overflow-hidden text-left">
        <span class="text-sm font-bold truncate text-on-surface">{{ userName }}</span>
        <div v-if="isLoggedIn" class="mt-1">
          <MemberTierBadge :tier="memberTierRaw" />
        </div>
        <span v-else class="text-xs text-on-surface-variant truncate">{{ userLevel }}</span>
      </div>
    </button>
  </aside>
</template>
